import {
  EXPERT_ICON,
  SKILL_ICON,
  TEMPLATE_ICON,
} from "@/components/display/display-icon/artifacts";
import { getDefaultKitIcon } from "@/components/display/display-name/defaults";
import { RoleGate } from "@/components/gate/RoleGate";
import { useAuthDetails } from "@/context/auth-context";
import { useGroupUserMeData } from "@/data/use-group-user-me-data";
import { useGroupRepositoryKitData } from "@/data/use-group-repository-kit-data";
import { useGroupData } from "@/data/use-groups-data";
import { useSettingData } from "@/data/use-settings-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid.tsx";
import { canOpenMembershipSettings } from "@/layouts/Guards/membership-settings-access";
import {
  faChartLine,
  faClipboardList,
  faDatabase,
  faPlug,
  faUsers,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import React from "react";
import { SidebarContentAdmin } from "./SidebarContentSettings";

export const GroupAdminMenu = () => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const { group } = useGroupData(groupUuid);
  const {
    organizationRole,
    groupRole,
    teamGroupAdmin,
    isLoading: isLoadingMembership,
  } = useGroupUserMeData(groupUuid);
  const authDetails = useAuthDetails();
  const isPersonalGroup = group?.personal ?? false;
  const isSuper = authDetails?.role === "super";
  const isGroupAdmin = groupRole === "admin";
  const canCreateTeam = isSuper || organizationRole === "admin";
  const showUsersManagement =
    !isPersonalGroup || canCreateTeam || teamGroupAdmin;
  const { value: sharedGroupUuid, isLoading: isLoadingSettings } =
    useSettingData(groupUuid, "SHARED_WORKSPACE_UUID");
  const { value: interviewerFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_INTERVIEWER",
  );
  const {
    isLoading: isLoadingGroupKnowledgeBaseKitData,
    kit: groupRepositoryKit,
  } = useGroupRepositoryKitData(groupUuid);

  if (isLoadingSettings || isLoadingMembership) {
    return null;
  }

  const creatorSettings: React.ComponentProps<
    typeof SidebarContentAdmin
  >["settings"] = [];

  if (groupUuid === sharedGroupUuid) {
    creatorSettings.push({
      name: "Shared Knowledge",
      link: {
        to: "/w/$groupSlug/settings/shared-group-knowledge",
        params: { groupSlug },
      },
      icon: faDatabase,
    });
  } else {
    creatorSettings.push(
      {
        name: "Knowledge",
        link: {
          to: "/w/$groupSlug/k/$kitUuid/knowledge",
          params: { groupSlug, kitUuid: groupRepositoryKit?.uuid },
        },
        icon: getDefaultKitIcon({ type: "repository" }),
        disabled: isLoadingGroupKnowledgeBaseKitData,
      },
      {
        name: "Shared Knowledge",
        link: {
          to: "/w/$groupSlug/settings/shared-knowledge",
          params: { groupSlug },
        },
        icon: faDatabase,
        disabled: isLoadingGroupKnowledgeBaseKitData,
      },
    );
  }

  creatorSettings.push(
    {
      name: "Document Templates",
      link: {
        to: "/w/$groupSlug/settings/templates",
        params: { groupSlug },
      },
      icon: TEMPLATE_ICON,
    },
    {
      name: "Workbook Templates",
      link: {
        to: "/w/$groupSlug/settings/presets",
        params: { groupSlug },
      },
      icon: getDefaultKitIcon({ type: "project_preset" }),
    },
    {
      name: "Experts",
      link: {
        to: "/w/$groupSlug/settings/experts",
        params: { groupSlug },
      },
      icon: EXPERT_ICON,
    },
    {
      name: "Skills",
      link: {
        to: "/w/$groupSlug/settings/skills",
        params: { groupSlug },
      },
      icon: SKILL_ICON,
    },
    {
      name: "Collections",
      link: {
        to: "/w/$groupSlug/settings/collections",
        params: { groupSlug },
      },
      icon: getDefaultKitIcon({ type: "collection" }),
    },
    {
      name: "Connectors",
      link: {
        to: "/w/$groupSlug/connectors",
        params: { groupSlug },
      },
      icon: faPlug,
    },
  );

  if (interviewerFeatureFlag === "enabled") {
    creatorSettings.push({
      name: "Interviews",
      link: {
        to: "/w/$groupSlug/settings/interviews",
        params: { groupSlug },
      },
      icon: faClipboardList,
    });
  }

  const usersManagement = {
    name: "Users Management",
    link: {
      to: "/w/$groupSlug/settings/users" as const,
      params: { groupSlug },
    },
    icon: faUsers,
  };

  const adminSettings: React.ComponentProps<
    typeof SidebarContentAdmin
  >["settings"] = [
    ...(isGroupAdmin && showUsersManagement ? [usersManagement] : []),
    {
      name: "Usage",
      systemRole: "super",
      link: {
        to: "/w/$groupSlug/settings/usage",
        params: { groupSlug },
      },
      icon: faChartLine,
    },
  ];

  return (
    <>
      <RoleGate groupRoles={["creator", "admin"]}>
        <SidebarContentAdmin title="Creation" settings={creatorSettings} />
      </RoleGate>
      <RoleGate groupRoles={["admin"]}>
        <SidebarContentAdmin title="Group" settings={adminSettings} />
      </RoleGate>
      {!isGroupAdmin &&
        canOpenMembershipSettings({
          isSuper,
          groupRole,
          organizationRole,
          teamGroupAdmin,
          isPersonalGroup,
        }) && (
          <SidebarContentAdmin title="Group" settings={[usersManagement]} />
        )}
    </>
  );
};
